import { Link } from 'react-router-dom';
import { useFavoritesStore } from '../context/FavoritesStore';
import { useCartStore } from '../context/CartStore';
import { FaTimes } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { motion, AnimatePresence } from 'framer-motion';

const FavoritesPage = () => {
  const favorites = useFavoritesStore((state) => state.favorites);
  const removeFavorite = useFavoritesStore((state) => state.removeFavorite);
  const addItem = useCartStore((state) => state.addItem);

  const handleAddToCart = (product) => {
    addItem({ ...product, quantity: 1 });
    toast.success(`Added to collection`);
  };

  const handleRemove = (id) => {
    removeFavorite(id);
  };

  if (favorites.length === 0) {
    return (
      <motion.div 
        initial={{ opacity: 0 }} animate={{ opacity: 1 }}
        className="min-h-[80vh] bg-deepDark flex items-center justify-center px-4"
      >
        <div className="text-center p-12 max-w-lg w-full border border-white/5 bg-surfaceDark/50">
          <h1 className="text-3xl font-serif text-white mb-6 tracking-wide">No Favorites Curated</h1>
          <p className="text-textMuted mb-10 text-sm font-light tracking-wide">Save exquisite pieces to review them later.</p>
          <div className="w-16 h-[1px] bg-gold mx-auto mb-10"></div>
          <Link to="/products" className="inline-block border border-gold text-gold px-12 py-4 text-xs font-bold tracking-widest uppercase hover:bg-gold hover:text-deepDark transition-colors duration-300">
            Explore Collection
          </Link>
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div 
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.8 }}
      className="min-h-screen bg-deepDark py-16 px-4"
    >
      <div className="max-w-7xl mx-auto">
        <div className="mb-12 border-b border-white/10 pb-6 flex items-baseline justify-between">
          <h1 className="text-4xl font-serif text-white tracking-wide">Favorites Vault</h1>
          <span className="text-textMuted text-xs tracking-widest uppercase">{favorites.length} Piece{favorites.length !== 1 ? 's' : ''}</span>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-8">
          <AnimatePresence>
            {favorites.map((product) => (
              <motion.div 
                key={product._id} 
                initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.95 }}
                className="bg-surfaceDark border border-white/5 flex flex-col group relative"
              >
                <div className="relative aspect-[4/5] bg-[#0a0a0a] overflow-hidden">
                  <Link to={`/product/${product._id}`} className="absolute inset-0 z-0">
                    <img
                      src={product.images?.[0] || '/placeholder.jpg'}
                      alt={product.name}
                      className="w-full h-full object-cover opacity-80 group-hover:opacity-100 group-hover:scale-105 transition-all duration-700"
                    />
                  </Link>
                  <button
                    onClick={() => handleRemove(product._id)}
                    className="absolute top-4 right-4 text-white/50 hover:text-gold z-10 transition-colors p-2 bg-deepDark/50 backdrop-blur-md rounded-none"
                    title="Remove"
                  >
                    <FaTimes size={12} />
                  </button>
                  {product.discount > 0 && <span className="absolute top-4 left-4 bg-gold text-deepDark px-3 py-1 font-bold text-[10px] tracking-widest uppercase z-10">{product.discount}% OFF</span>}
                </div>

                <div className="p-6 flex flex-col flex-1">
                  <Link to={`/product/${product._id}`} className="hover:text-gold transition-colors mb-2">
                    <h3 className="text-lg font-serif text-white line-clamp-1">{product.name}</h3>
                  </Link>
                  <p className="text-[10px] tracking-widest uppercase text-textMuted mb-4">{product.material}</p>

                  <div className="flex gap-3 items-baseline mb-6">
                    <span className="text-xl font-serif text-gold">₹{product.discount > 0 ? (product.price * (1 - product.discount / 100)).toFixed(0) : product.price}</span>
                    {product.discount > 0 && <span className="text-xs text-textMuted line-through">₹{product.price}</span>}
                  </div>

                  <button
                    className="w-full mt-auto bg-transparent border border-white/20 text-white font-bold py-3 text-[10px] tracking-widest uppercase hover:border-gold hover:text-gold transition-colors disabled:opacity-20"
                    onClick={() => handleAddToCart(product)}
                    disabled={product.stock < 1}
                  >
                    {product.stock >= 1 ? 'Acquire' : 'Waitlist'}
                  </button>
                </div>
              </motion.div>
            ))}
          </AnimatePresence>
        </div>
      </div>
    </motion.div>
  );
};

export default FavoritesPage;
